import { Order, OrderStatus, OrderType } from "@/models";
import AcceptOrderDrawer from "./AcceptOrderDrawer";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import CompleteOrderDrawer from "./CompleteOrderDrawer";
import { useOrders } from "@/contexts";
import { Skeleton } from "./ui/skeleton";
import { Button } from "./ui/button/button";
import { ButtonSize, ButtonType } from "./ui/button/button.type";
import CancelledOrderDrawer from "./CancelledOrderDrawer";
import { CancelOrder } from "./cancelOrder";
import CompletedOrderDrawer from "./CompletedOrderDrawer";
import { useEffect, useState } from "react";
import { upiIdDisplay } from "@/lib/utils";
import moment from "moment";
import { trackAmplitudeEvent } from "@/utils/amplitude";

export const OrderDrawer = ({
  autoOpen,
  onClose,
  order,
  TriggerComponent,
  isLoading,
  archived,
}: any) => {
  const [isOrderExpire, setIsOrderExpire] = useState(false);
  const drawerParams: { open?: boolean } = {};
  const { refreshOrders } = useOrders();

  if (autoOpen !== undefined) {
    drawerParams.open = autoOpen;
  }
  const archivedIds = JSON.parse(
    localStorage.getItem("archivedIds") || "[]",
  ).map(Number);
  const isArchived = archivedIds.includes(Number(order?.id));

  const loaderView = (
    <div className="mb-[16px]">
      <DrawerFooter className="pt-0">
        <Skeleton className="h-[24px] w-[102px] bg-bg-grey" />
        <Skeleton className="h-[24px] w-[102px] bg-bg-grey" />
        <Skeleton className="h-[44px] w-full bg-bg-grey" />
        <DrawerClose
          onClick={() => {
            onClose?.();
          }}>
          <div className="w-full rounded-md border-[1px] border-black bg-bg-primary p-2 font-bold">
            Close
          </div>
        </DrawerClose>
      </DrawerFooter>
    </div>
  );
  const handleArchive = (orderId: string) => {
    const archivedIds = JSON.parse(localStorage.getItem("archivedIds") || "[]");
    if (!archivedIds.includes(orderId)) {
      if (
        window.confirm(
          "Are you sure you want to archive this order? If you archive this order, you will be able to view it in the Archived orders page only!.",
        )
      ) {
        trackAmplitudeEvent("ORDER_ARCHIVED");
        archivedIds.push(orderId);
        localStorage.setItem("archivedIds", JSON.stringify(archivedIds));
        onClose();
        refreshOrders();
        return;
      }
    }

    if (archivedIds.includes(orderId)) {
      const orderDate = new Date(Number(order.placedTimestamp) * 1000);
      const currentDate = new Date();
      const hoursDifference =
        (currentDate.getTime() - orderDate.getTime()) / (1000 * 60 * 60);

      if (hoursDifference < 24) {
        if (window.confirm("Are you sure you want to unarchive this order?")) {
          trackAmplitudeEvent("ORDER_UNARCHIVED");
          const index = archivedIds.indexOf(orderId);
          if (index > -1) {
            archivedIds.splice(index, 1);
            localStorage.setItem("archivedIds", JSON.stringify(archivedIds));
          }
          onClose();
          refreshOrders();
        }
      } else {
        alert("You can only unarchive orders within 24 hours of archiving.");
      }
    }
    return;
  };
  useEffect(() => {
    let interval: any;

    const checkOrderExpiry = () => {
      const qrCode = upiIdDisplay(order?.encUpi)?.qrCode;
      const tempQr = upiIdDisplay(order?.encUpi)?.tempQr;

      if (!tempQr) {
        clearInterval(interval);
        return;
      }

      const qrExpiry = qrCode
        ? new URLSearchParams(qrCode.split("?")[1]).get("QRexpire")
        : null;
      const currentTime = Date.now();
      const placedTime = new Date(
        Number(order?.placedTimestamp) * 1000,
      ).getTime();
      const timeElapsed = currentTime - placedTime;

      const QRexpiryTime = new URLSearchParams(qrCode.split("?")[1]).get(
        "QRexpire",
      );

      let timeDifferenceInMinutes: any = null;
      if (QRexpiryTime) {
        const formattedQRexpiryTime = QRexpiryTime.replace(" ", "+");
        const expiryDateTime = moment(
          formattedQRexpiryTime,
          moment.ISO_8601,
          true,
        );
        const currentDateTime = moment();
        timeDifferenceInMinutes = expiryDateTime.diff(
          currentDateTime,
          "minutes",
        );
      }

      if (
        (!qrExpiry && timeElapsed > 150000) ||
        (timeDifferenceInMinutes <= 1 && timeDifferenceInMinutes != null)
      ) {
        console.log("Disabling Click to Pay button");
        trackAmplitudeEvent("ORDER_EXPIRED");
        setIsOrderExpire(true);
        clearInterval(interval);
      }
    };

    if (order?.encUpi) {
      interval = setInterval(checkOrderExpiry, 1000);
    }

    return () => {
      clearInterval(interval);
    };
  }, [order?.encUpi]);

  function getOrderDrawer(
    order: Order,
    isLoading: boolean,
    onClose: () => void,
  ) {
    if (order.status === OrderStatus.CANCELLED) {
      return (
        <CancelledOrderDrawer
          order={order}
          onClose={onClose}
          isLoading={isLoading}
        />
      );
    } else if (order.status == OrderStatus.COMPLETED) {
      return (
        <CompletedOrderDrawer
          order={order}
          onClose={onClose}
          isLoading={isLoading}
        />
      );
    } else if (
      order.status === OrderStatus.ACCEPTED ||
      order.status === OrderStatus.PAID
    ) {
      return (
        <CompleteOrderDrawer
          order={order}
          onClose={onClose}
          isLoading={isLoading}
          isExpire={isOrderExpire}
        />
      );
    } else {
      return (
        <AcceptOrderDrawer
          order={order}
          onClose={onClose}
          isLoading={isLoading}
        />
      );
    }
  }

  return (
    <Drawer
      {...drawerParams}
      onClose={() => {
        onClose?.();
      }}>
      <DrawerTrigger className="flex flex-col items-center justify-center">
        {TriggerComponent}
      </DrawerTrigger>
      <DrawerContent className="bg-bg-tertiary">
        <DrawerHeader className="flex flex-col">
          <DrawerTitle className="font-satoshiBlack text-center font-bold text-text-light">
            Order
          </DrawerTitle>
          <DrawerDescription className="flex justify-between">
            <div className="flex-column flex w-[20%] max-w-[80px] items-center justify-center rounded-md bg-bg-primary text-center text-xs font-bold text-black">
              ID: {order && order?.id}
            </div>
            <div className="flex">
              {order?.orderType == OrderType.SELL &&
                order?.status != OrderStatus.CANCELLED &&
                order?.status != OrderStatus.PLACED && (
                  <div className="mr-2">
                    <CancelOrder orderId={order.id} callback={onClose} />
                  </div>
                )}
              {order &&
                order.status >= 1 &&
                order.status !== OrderStatus.CANCELLED && (
                  <>
                    {isArchived &&
                    archived &&
                    order.status !== OrderStatus.COMPLETED ? (
                      <Button
                        label="UnArchive"
                        type={ButtonType.danger}
                        size={ButtonSize.small}
                        onClick={() => handleArchive(order.id)}
                      />
                    ) : (
                      !archived &&
                      !isArchived &&
                      order.status !== OrderStatus.COMPLETED && (
                        <Button
                          label="Archive"
                          type={ButtonType.danger}
                          size={ButtonSize.small}
                          onClick={() => handleArchive(order.id)}
                        />
                      )
                    )}
                  </>
                )}
            </div>
          </DrawerDescription>
        </DrawerHeader>
        {!order ? loaderView : <>{getOrderDrawer(order, isLoading, onClose)}</>}
      </DrawerContent>
    </Drawer>
  );
};
